import {
  ChangeEventHandler,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnProps } from 'antd/lib/table';
import { Col, Form, Input, Row, Select, Space, Table } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { Link, useSearchParams } from 'react-router-dom';
import {
  DOCTOR_STATUS,
  DOCTOR_STATUSES,
  DOCTOR_STATUS_LABEL
} from 'constants/doctors';
import { PERMISSION_ACTIONS, PERMISSION_ROUTES } from 'constants/permissions';
import { fetchDoctors } from 'services/api/doctors';
import { DoctorListRow } from 'types/api/doctors';

import useOrgEffect from 'hooks/useOrgEffect';
import { showErrorMessage } from 'utils/showErrorMessage';
import { IconButton, PageWrapper } from 'components/ui';
import { AddDoctor } from './components';
import { Private } from 'components/shared';

const { Search } = Input;
const { Option } = Select;
const PAGE_LIMIT = 10;

const Doctors: FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState<string>(DOCTOR_STATUSES.all);
  const [search, setSearch] = useState('');

  const page = Number(searchParams.get('page')) || 1;

  const [results, setResults] = useState<DoctorListRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  useOrgEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const res = await fetchDoctors({
          page,
          limit: PAGE_LIMIT,
          status: status === DOCTOR_STATUSES.all ? undefined : status,
          name: search || undefined
        });

        if (cancelled) return;
        setResults(res.results);
        setTotal(res.total);
      } catch (err) {
        if (!cancelled) showErrorMessage(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [page, status, search, refreshKey]);

  const refreshList = useCallback(() => {
    setRefreshKey(k => k + 1);
  }, []);

  // refs
  const timer = useRef<NodeJS.Timeout | null>(null);

  const columns: ColumnProps<DoctorListRow>[] = useMemo(
    () => [
      {
        title: t('id'),
        dataIndex: 'id',
        key: 'id'
      },
      {
        title: t('table.name'),
        dataIndex: 'name',
        key: 'name',
        render: (_, record) => `${record.first_name} ${record.last_name}`
      },
      {
        title: t('table.email'),
        dataIndex: 'email',
        key: 'email',
        render: value => <a href={`mailto:${value}`}>{value}</a>
      },
      {
        title: t('table.phone'),
        dataIndex: 'phone',
        key: 'phone',
        render: value => <a href={`tel:${value}`}>{value}</a>
      },
      {
        title: t('table.status'),
        dataIndex: 'status',
        key: 'status',
        render: value =>
          !value
            ? t('doctors.waiting')
            : t(
                `doctors.${
                  DOCTOR_STATUS_LABEL[value as keyof typeof DOCTOR_STATUS_LABEL]
                }`
              )
      },
      {
        title: '',
        dataIndex: 'action',
        key: 'id',
        width: '5%',
        align: 'right',
        render: (_, record) => (
          <Link to={`${record.id}/personal-information?lang=en`}>
            <IconButton>
              <EyeOutlined />
            </IconButton>
          </Link>
        )
      }
    ],
    [t]
  );

  const onSearchChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    e => {
      if (page > 0) {
        searchParams.set('page', '0');
        setSearchParams(searchParams);
      }

      if (timer.current) {
        clearTimeout(timer.current);
      }

      timer.current = setTimeout(() => {
        setSearch(e.target.value);
      }, 500);
    },
    [page, searchParams, setSearchParams]
  );

  useEffect(() => {
    if (searchParams.get('status')) {
      setStatus(searchParams.get('status') || DOCTOR_STATUSES.active);
    }
  }, [searchParams, status]);

  const onStatusChange = useCallback(
    (val: string) => {
      searchParams.set('page', '0');
      searchParams.set('status', val);
      setSearchParams(searchParams);
      setStatus(val);
    },
    [searchParams, setSearchParams]
  );

  return (
    <PageWrapper
      title={t('doctors.page_title')}
      color="white"
      extra={[
        <Private
          key="add-doctor"
          route={PERMISSION_ROUTES.doctors}
          action={PERMISSION_ACTIONS.create}
        >
          <AddDoctor onSaved={refreshList} />
        </Private>
      ]}
    >
      <Row>
        <Col sm={8}>
          <Space>
            <Form.Item>
              <Search
                placeholder={t('common.search')}
                onChange={onSearchChange}
              />
            </Form.Item>
            <Form.Item>
              <Select
                defaultValue={searchParams.get('status') || DOCTOR_STATUSES.all}
                style={{ width: 120 }}
                onChange={onStatusChange}
              >
                {DOCTOR_STATUS.map(item => (
                  <Option key={item.value} value={item.value}>
                    {t(`doctors.${item.label}`)}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Space>
        </Col>
      </Row>
      <Table
        dataSource={results}
        columns={columns}
        loading={loading}
        rowKey="id"
        pagination={{
          total,
          current: page,
          onChange: p => {
            searchParams.set('page', `${p}`);
            setSearchParams(searchParams);
          },
          showSizeChanger: false
        }}
      />
    </PageWrapper>
  );
};

export default Doctors;
